import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SEOHead from '@/components/feature/SEOHead';

const schema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Conditions Générales de Vente - CasaReNovaPlus",
  "url": `${import.meta.env.VITE_SITE_URL}/cgv`,
  "description": "Conditions générales de vente de HB SERVICES, entreprise CVSE à Genève.",
  "publisher": { "@type": "Organization", "name": "CasaReNovaPlus" }
};

export default function CGV() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="CGV - Conditions Générales de Vente CasaReNovaPlus Neuenegg"
        description="Conditions générales de vente de HB SERVICES à Genève. Devis, paiement, garanties, délais d'exécution et responsabilités pour tous travaux CVSE en Suisse romande."
        keywords="CGV HB SERVICES, conditions générales vente Genève, garanties travaux CVSE Suisse"
        canonical={`${import.meta.env.VITE_SITE_URL}/cgv`}
        schema={schema}
      />
      <Header />
      <main className="py-16 px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Conditions Générales de Vente</h1>
          
          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Objet</h2>
              <p className="leading-relaxed mb-4">
                Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre HB SERVICES et ses clients 
                pour toute prestation de services ou vente de produits dans le cadre de ses activités, notamment :
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li className="leading-relaxed">Activités techniques incluant l'installation, l'exécution, la maintenance, le dépannage et le suivi d'équipements et d'installations techniques.</li>
                <li className="leading-relaxed">Prestations de nettoyage et d'entretien pour particuliers, entreprises et locaux professionnels, incluant le nettoyage courant et spécialisé.</li>
                <li className="leading-relaxed">Services de conciergerie, gestion et entretien d'immeubles.</li>
                <li className="leading-relaxed">Achat, vente, importation, exportation et distribution de matériel, produits et équipements liés aux domaines d'activité.</li>
                <li className="leading-relaxed">Services de formation et de conseil dans les secteurs du nettoyage, de la maintenance et des services techniques.</li>
                <li className="leading-relaxed">Toutes opérations commerciales, financières ou techniques se rattachant directement ou indirectement au but principal.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Devis et Commandes</h2>
              <p className="leading-relaxed mb-3">
                <strong>2.1 Devis :</strong> Tout devis établi par HB SERVICES est valable 30 jours à compter de sa date d'émission. 
                Les prix indiqués sont en francs suisses (CHF) et incluent la TVA au taux en vigueur.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>2.2 Commande :</strong> La commande devient ferme et définitive après signature du devis par le client 
                et versement de l'acompte éventuel.
              </p>
              <p className="leading-relaxed">
                <strong>2.3 Modifications :</strong> Toute modification demandée par le client après acceptation du devis 
                fera l'objet d'un avenant et pourra entraîner une révision des prix et délais.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Prix et Paiement</h2>
              <p className="leading-relaxed mb-3">
                <strong>3.1 Prix :</strong> Les prix sont établis en fonction des tarifs en vigueur au moment de l'établissement du devis. 
                Ils comprennent la main-d'œuvre, les fournitures, le matériel et les frais de déplacement dans un rayon de 30 km.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>3.2 Modalités de paiement :</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Acompte de 30% à la commande pour les travaux supérieurs à 5'000 CHF</li>
                <li>Paiement intermédiaire de 40% à mi-parcours pour les chantiers importants</li>
                <li>Solde à la fin des travaux, payable sous 30 jours</li>
              </ul>
              <p className="leading-relaxed">
                <strong>3.3 Retard de paiement :</strong> En cas de retard de paiement, des intérêts moratoires au taux de 5% l'an 
                seront appliqués automatiquement, sans mise en demeure préalable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Délais d'Exécution</h2>
              <p className="leading-relaxed mb-3">
                Les délais d'exécution sont donnés à titre indicatif et peuvent varier en fonction des conditions météorologiques, 
                de la disponibilité des matériaux ou de circonstances indépendantes de notre volonté.
              </p>
              <p className="leading-relaxed">
                HB SERVICES s'engage à informer le client dans les meilleurs délais de tout retard prévisible.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Garanties</h2>
              <p className="leading-relaxed mb-3">
                <strong>5.1 Garantie légale :</strong> Tous nos travaux sont garantis conformément à la législation suisse en vigueur. 
                La garantie couvre les défauts de matériaux et de main-d'œuvre.
              </p>
              <p className="leading-relaxed mb-3">
                <strong>5.2 Durée de garantie :</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-3">
                <li>Installations électriques : 2 ans</li>
                <li>Pompes à chaleur : 2 ans (pièces) + garantie constructeur</li>
                <li>Installations sanitaires : 2 ans</li>
                <li>Systèmes de ventilation : 2 ans</li>
                <li>Domotique : 1 an</li>
                <li>Prestations de tous types de travaux de rénovation avec reference : satisfaction garantie ou reprise sans frais</li>
                <li>Services de conciergerie et gestion d'immeubles : selon contrat spécifique</li>
                <li>Matériel et équipements distribués : garantie constructeur applicable</li>
              </ul>
              <p className="leading-relaxed">
                <strong>5.3 Exclusions :</strong> La garantie ne couvre pas l'usure normale, les dommages résultant d'une mauvaise utilisation, 
                d'un défaut d'entretien ou d'une intervention par un tiers non autorisé.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Responsabilités</h2>
              <p className="leading-relaxed mb-3">
                HB SERVICES est assurée en responsabilité civile professionnelle. Notre responsabilité est limitée aux dommages 
                directs causés par nos prestations, dans la limite du montant de la commande.
              </p>
              <p className="leading-relaxed">
                Le client s'engage à fournir un accès libre et sécurisé au chantier et à signaler tout danger potentiel.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Annulation et Résiliation</h2>
              <p className="leading-relaxed mb-3">
                <strong>7.1 Par le client :</strong> Toute annulation doit être notifiée par écrit. En cas d'annulation après le début 
                des travaux, les prestations déjà effectuées seront facturées intégralement.
              </p>
              <p className="leading-relaxed">
                <strong>7.2 Par HB SERVICES :</strong> Nous nous réservons le droit d'annuler une commande en cas de force majeure 
                ou si les conditions de sécurité ne sont pas réunies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Service d'Urgence</h2>
              <p className="leading-relaxed">
                Notre service d'urgence 24/7 est disponible pour les interventions urgentes. Les tarifs d'urgence (majorés de 50% 
                en soirée/week-end et 100% les jours fériés) s'appliquent en dehors des heures ouvrables normales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Protection des Données</h2>
              <p className="leading-relaxed">
                Les données personnelles collectées sont utilisées uniquement dans le cadre de l'exécution des prestations 
                et conformément à la loi fédérale sur la protection des données (LPD). Elles ne sont jamais transmises à des tiers 
                sans votre consentement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Droit Applicable et Juridiction</h2>
              <p className="leading-relaxed mb-3">
                Les présentes CGV sont régies par le droit suisse. En cas de litige, les parties s'efforceront de trouver 
                une solution amiable.
              </p>
              <p className="leading-relaxed">
                À défaut d'accord, les tribunaux du canton de Genève seront seuls compétents.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Acceptation des CGV</h2>
              <p className="leading-relaxed">
                La signature du devis ou le paiement de l'acompte vaut acceptation sans réserve des présentes Conditions Générales de Vente.
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>CasaReNovaPlus</strong><br />
                Austrasse 95, 3176 Neuenegg — info@casarenovaplus.ch — +41 76 480 31 88 / +41 76 565 42 03<br />
                Date de dernière mise à jour : Avril 2026<br />
                Pour toute question concernant ces CGV, contactez-nous via notre formulaire de contact.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
